import { useEffect, useMemo, useState } from 'react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TASK_CONST } from '@/constant';
import { useInfiniteTaskListQuery } from '@/hook';
import {
  Close,
  DriveFolderUpload,
  RadioButtonChecked,
  RadioButtonUnchecked,
  VButton,
  VDialog,
  VFlex,
  VInput,
  VStack,
  VTable,
  cn,
  useUnleash,
} from '@/package';
import type { ModelTrainingForm, Task } from '@/type';

import SplitCardContainer from './SplitCardContainer';

type DataSettingProps = {
  setValue: UseFormSetValue<ModelTrainingForm>;
  watch: UseFormWatch<ModelTrainingForm>;
  updateTaskSplitRatio: (
    taskId: Task['id'],
    training: number,
    validation: number,
  ) => void;
  getTaskSplitRatio: (taskId: Task['id']) => {
    training: number;
    validation: number;
  };
};

const DataSetting = ({
  setValue,
  watch,
  updateTaskSplitRatio,
  getTaskSplitRatio,
}: DataSettingProps) => {
  const { getVariant } = useUnleash();

  const modeList = [
    {
      title: 'Auto split',
      description: 'Automatically splits the data into optimal proportions.',
      isSelected: watch('dataSettingMode') === 'auto',
      mode: 'auto' as ModelTrainingForm['dataSettingMode'],
    },
    {
      title: 'User split',
      description: 'Allows users to manually define the split ratio.',
      isSelected: watch('dataSettingMode') === 'manual',
      mode: 'manual' as ModelTrainingForm['dataSettingMode'],
    },
  ];

  useEffect(() => {
    if (!watch('dataSettingMode')) {
      setValue('dataSettingMode', 'auto');
    }
  }, []);

  return (
    <VStack className="w-full">
      <VFlex gap={1} className="w-full">
        {modeList.map(({ title, description, isSelected, mode }) => {
          const Icon = isSelected ? RadioButtonChecked : RadioButtonUnchecked;
          const userSplit = getVariant('vuex.detroit.trainingDataConfig');
          const isUserSplit = userSplit?.feature_enabled;
          const disabled = !isUserSplit && mode === 'manual';

          return (
            <VStack
              className={cn('flex-1 rounded-md p-6 text-primary-500', {
                'border border-blue-300 bg-Primary-Navy-Active': isSelected,
                'border border-gray-300 bg-white': !isSelected,
              })}
              style={
                disabled
                  ? {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      cursor: 'not-allowed',
                    }
                  : {}
              }
              gap={0.5}
              key={title}
              role="button"
              onClick={() => {
                if (disabled) return;
                setValue('dataSettingMode', mode);
              }}
            >
              <VFlex justify="between">
                <div className="text-headline-sm font-bold">{title}</div>
                <Icon className="size-5" />
              </VFlex>
              <p className="text-body-md">{description}</p>
            </VStack>
          );
        })}
      </VFlex>
      <DataSettingOperation
        setValue={setValue}
        watch={watch}
        updateTaskSplitRatio={updateTaskSplitRatio}
        getTaskSplitRatio={getTaskSplitRatio}
      />
    </VStack>
  );
};

type DataSettingOperationProps = {
  setValue: UseFormSetValue<ModelTrainingForm>;
  watch: UseFormWatch<ModelTrainingForm>;
  updateTaskSplitRatio: (
    taskId: Task['id'],
    training: number,
    validation: number,
  ) => void;
  getTaskSplitRatio: (taskId: Task['id']) => {
    training: number;
    validation: number;
  };
};

const DataSettingOperation = ({
  setValue,
  watch,
  updateTaskSplitRatio,
  getTaskSplitRatio,
}: DataSettingOperationProps) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [tempSelectedTaskList, setTempSelectedTaskList] = useState<Task[]>([]);
  const { data: taskListData } = useInfiniteTaskListQuery({
    taskState: [TASK_CONST.TASK_WORKSTATE.COMPLETED],
    hasObject: true,
  });

  const selectedTaskList = watch('selectedTaskList');
  const taskList =
    taskListData?.pages.map(page => page.data.resultData).flat() ?? [];

  const handleOpenDialog = () => {
    setTempSelectedTaskList([...selectedTaskList]);
    setVisible(true);
  };

  const handleCancel = () => {
    setTempSelectedTaskList([...selectedTaskList]);
    setVisible(false);
  };

  const handleComplete = () => {
    setValue('selectedTaskList', tempSelectedTaskList);
    setVisible(false);
  };

  const renderedTaskList = useMemo(() => {
    return taskList.map(task => ({
      checked: tempSelectedTaskList.find(t => t.id === task.id),
      name: task.taskName,
      id: `${task.id}`,

      task,
    }));
  }, [taskList, tempSelectedTaskList]);

  const selectedTask = watch('selectedTaskList');

  return (
    <>
      <VStack gap={1.5} className="mt-4">
        <div className="h-px w-full bg-gray-300" />

        <VStack className="w-full" gap={1}>
          <VFlex justify="between" items="center">
            <VFlex gap={0.5} items="center">
              <h3 className="text-headline-sm font-bold text-blue-300">
                {t('modeling.dataSetting.pessoner')}
              </h3>
              <div className="h-4 w-px bg-gray-300" />
              <span className="text-body-lg font-semibold text-blue-300">
                {t('common.total.cerebralism', { total: selectedTask.length })}
              </span>
            </VFlex>

            <VButton size="md" onClick={handleOpenDialog}>
              <DriveFolderUpload className="size-4" />
              {t('modeling.selectData.sphaerococcus')}
            </VButton>
          </VFlex>

          {selectedTask.length === 0 ? (
            <VStack
              className="w-full rounded-md border border-gray-200 py-4"
              items="center"
              gap={0.25}
            >
              <DriveFolderUpload className="size-12 text-gray-300" />
              <p className="text-body-xl text-gray-300">
                {t('modeling.selectSomeDataToGetStarted.togetherness')}
              </p>
            </VStack>
          ) : (
            <SplitCardContainer
              disabled={false}
              watch={watch}
              setValue={setValue}
              updateTaskSplitRatio={updateTaskSplitRatio}
              getTaskSplitRatio={getTaskSplitRatio}
            />
          )}
        </VStack>
      </VStack>

      <VDialog
        dialogMainClass="w-[1020px] h-[657px] !bg-white"
        visible={visible}
        onUpdateVisible={setVisible}
      >
        <VStack className="h-full p-6" gap={1}>
          <VStack gap={0.5} className="w-full flex-1 overflow-y-hidden">
            <VFlex justify="between" className="w-full" items="center">
              <h2 className="text-headline-md font-bold">
                {t('modeling.selectData.sphaerococcus')}
              </h2>
              <Close
                role="button"
                className="size-6 text-gray-400"
                onClick={handleCancel}
              />
            </VFlex>

            <VFlex className="w-full" justify="between" items="center">
              <div className="text-body-lg font-semibold text-blue-300">
                {t('common.selected.tenuifasciate', {
                  selected: tempSelectedTaskList.length,
                })}
              </div>
              <VInput
                placeholder={t('development.searchInputPlaceholder.photopile')}
                type="search"
              />
            </VFlex>

            <VTable
              data={renderedTaskList}
              className="flex-1 overflow-y-auto"
              columnList={['checked', 'id', 'name']}
              columnStyleMap={{
                checked: { className: 'w-[36px]' },
                id: { className: 'w-[72px]' },
              }}
              columnRenderer={{
                id: () => <div>Task ID</div>,
                name: () => <div>Data name</div>,
              }}
              cellRenderer={{
                id: (_1, _2, row) => {
                  return (
                    <VFlex justify="center" items="center" className="w-full">
                      {row.task.id}
                    </VFlex>
                  );
                },
                name: (_1, _2, row) => {
                  return (
                    <VStack className="gap-[2px] py-2">
                      <div className="text-body-xl">{row.task.taskName}</div>
                      <div className="text-body-lg text-gray-400">
                        {row.task.project?.name ?? '-'}
                      </div>
                    </VStack>
                  );
                },
              }}
              setRowChecked={row => !!row.checked}
              onRowCheckedChange={row => {
                row.checked
                  ? setTempSelectedTaskList(
                      tempSelectedTaskList.filter(t => t.id !== row.task.id),
                    )
                  : setTempSelectedTaskList([
                      ...tempSelectedTaskList,
                      row.task,
                    ]);
              }}
              onAllRowCheckedChange={checked => {
                checked
                  ? setTempSelectedTaskList([])
                  : setTempSelectedTaskList(
                      renderedTaskList.map(task => task.task),
                    );
              }}
            />
          </VStack>

          <VFlex justify="between" items="center">
            <VButton
              className="w-[308px]"
              outlined
              size="lg"
              variant="secondary"
              onClick={handleCancel}
            >
              {t('modal.cancelButtonText.intimidate')}
            </VButton>
            <VButton size="lg" className="w-[308px]" onClick={handleComplete}>
              {t('modal.complete.mantid')}
            </VButton>
          </VFlex>
        </VStack>
      </VDialog>
    </>
  );
};

export default DataSetting;
